import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPhone, FiMail, FiMapPin, FiClock, FiMessageSquare, FiSend, FiUser } = FiIcons;

const Contact = () => {
  const [ref, inView] = useInView({
    threshold: 0.3,
    triggerOnce: true
  });

  const contactInfo = [
    {
      icon: FiPhone,
      title: 'Phone',
      details: '+94 47 2226621',
      subtitle: '24/7 Safari Booking Hotline',
      action: 'tel:+94472226621'
    },
    {
      icon: FiMail,
      title: 'Email',
      details: 'info@hotelathgira.com',
      subtitle: 'Quick response within 2 hours',
      action: 'mailto:info@hotelathgira.com'
    },
    {
      icon: FiMapPin,
      title: 'Location',
      details: 'Udawalawe Road, Udawalawe',
      subtitle: 'Opposite Elephant Transit Home',
      action: 'https://maps.google.com'
    },
    {
      icon: FiClock,
      title: 'Reception Hours',
      details: '24/7 Available',
      subtitle: 'Always here for your safari needs',
      action: null
    }
  ];

  const directions = [
    {
      from: 'From Colombo',
      distance: '165 km (3.5 hours)',
      route: 'A4 Highway → Ratnapura → Embilipitiya → Udawalawe'
    },
    {
      from: 'From Ella',
      distance: '95 km (2.5 hours)',
      route: 'Wellawaya → Thanamalwila → Udawalawe'
    },
    {
      from: 'From Galle',
      distance: '140 km (3 hours)',
      route: 'A2 → Matara → A18 → Embilipitiya → Udawalawe'
    }
  ];

  return (
    <section id="contact" className="py-20 bg-gradient-to-br from-gray-50 to-orange-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-playfair font-bold text-gray-900 mb-6">
            Plan Your <span className="gradient-text">Safari Adventure</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto font-inter">
            Ready to experience authentic safari hospitality? Contact us to book your stay 
            at the original safari basecamp in the heart of Udawalawe.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-16">
          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <h3 className="text-3xl font-playfair font-bold text-gray-900 mb-8">
              Get in Touch
            </h3>

            <div className="space-y-6 mb-12">
              {contactInfo.map((info, index) => (
                <motion.div
                  key={info.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
                  className="glass-effect rounded-2xl p-6 hover-lift group"
                >
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 liquid-gradient rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <SafeIcon icon={info.icon} className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-gray-900 mb-1">{info.title}</h4>
                      {info.action ? (
                        <a
                          href={info.action}
                          className="text-orange-600 font-semibold hover:text-orange-700 transition-colors"
                        >
                          {info.details}
                        </a>
                      ) : (
                        <p className="text-orange-600 font-semibold">{info.details}</p>
                      )}
                      <p className="text-gray-600 text-sm font-inter">{info.subtitle}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Directions */}
            <h4 className="text-xl font-playfair font-bold text-gray-900 mb-6">
              How to Reach Us
            </h4>
            <div className="space-y-4">
              {directions.map((direction, index) => (
                <motion.div
                  key={direction.from}
                  initial={{ opacity: 0, x: -20 }}
                  animate={inView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.6, delay: 0.8 + index * 0.1 }}
                  className="glass-effect rounded-xl p-4"
                >
                  <div className="flex justify-between items-center mb-2">
                    <h5 className="font-semibold text-gray-900">{direction.from}</h5>
                    <span className="text-orange-600 font-semibold text-sm">{direction.distance}</span>
                  </div>
                  <p className="text-gray-600 text-sm font-inter">{direction.route}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="glass-effect rounded-3xl p-8"
          >
            <h3 className="text-2xl font-playfair font-bold text-gray-900 mb-6">
              Send Us a Message
            </h3>
            
            <form className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-gray-700 font-semibold mb-2 font-inter">
                    Full Name
                  </label>
                  <div className="relative">
                    <SafeIcon icon={FiUser} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-all duration-200 font-inter"
                      placeholder="Your full name"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-gray-700 font-semibold mb-2 font-inter">
                    Email Address
                  </label>
                  <div className="relative">
                    <SafeIcon icon={FiMail} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="email"
                      className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-all duration-200 font-inter"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-gray-700 font-semibold mb-2 font-inter">
                    Check-in Date
                  </label>
                  <input
                    type="date"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-all duration-200 font-inter"
                  />
                </div>
                
                <div>
                  <label className="block text-gray-700 font-semibold mb-2 font-inter">
                    Check-out Date
                  </label>
                  <input
                    type="date"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-all duration-200 font-inter"
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2 font-inter">
                  Number of Guests
                </label>
                <select className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-all duration-200 font-inter">
                  <option>1 Guest</option>
                  <option>2 Guests</option>
                  <option>3 Guests</option>
                  <option>4 Guests</option>
                  <option>5+ Guests</option>
                </select>
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2 font-inter">
                  Message
                </label>
                <div className="relative">
                  <SafeIcon icon={FiMessageSquare} className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
                  <textarea
                    rows="4"
                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-all duration-200 font-inter"
                    placeholder="Tell us about your safari preferences, special requests, or any questions..."
                  ></textarea>
                </div>
              </div>

              <motion.button
                type="submit"
                className="w-full bg-gradient-to-r from-orange-600 to-red-600 text-white py-4 rounded-xl font-semibold text-lg ripple-effect flex items-center justify-center space-x-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <SafeIcon icon={FiSend} className="w-5 h-5" />
                <span>Send Message</span>
              </motion.button>
            </form>

            {/* Quick Booking Note */}
            <div className="mt-6 p-4 bg-orange-50 rounded-xl">
              <p className="text-orange-700 text-sm font-inter">
                <strong>Quick Booking:</strong> For immediate reservations, call us at +94 47 2226621. 
                Our safari experts are available 24/7 to help plan your perfect Udawalawe experience.
              </p>
            </div>
          </motion.div>
        </div>

        {/* Map Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 1.0 }}
          className="mt-16 glass-effect rounded-3xl overflow-hidden"
        >
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-2xl font-playfair font-bold text-gray-900">
              Our Prime Location
            </h3>
            <p className="text-gray-600 font-inter">
              Directly opposite the Udawalawe Elephant Transit Home - the closest accommodation to both the park and the sanctuary.
            </p>
          </div>
          <div className="h-64 bg-gradient-to-br from-orange-100 to-red-100 flex items-center justify-center">
            <div className="text-center">
              <SafeIcon icon={FiMapPin} className="w-12 h-12 text-orange-600 mx-auto mb-4" />
              <p className="text-gray-700 font-semibold">Interactive Map</p>
              <p className="text-gray-600 text-sm">Google Maps integration would be placed here</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;